// pages/ProjectApproval/DeptProjects.js
//
// Legacy menu: "View My Department's Projects" — under Project
// Approval System.
//
// Access (Phase 3 role-based):
//   - Manager (RoleId 5) → sees self + direct reports' projects
//   - President / President-CEO / IT Admin → see ALL projects
//   - Anyone else → 403 (Phase 3 RoleProtected requireManager)
//
// API: GET /api/ProjectApproval/my-department  (built in Phase 3 Pass 2)

import React, { useEffect, useState, useMemo } from "react";
import { ColumnFilterRow, applyColumnFilters } from "../../Components/Common/DashboardKit";
import useTableSort from "../../Components/Common/DashboardKit/useTableSort";
import SortableTh from "../../Components/Common/DashboardKit/SortableTh";
import exportRowsToCsv from "../../Components/Common/DashboardKit/exportCsv";
import { Container, Card, CardBody, Input, InputGroup, Badge, Alert } from "reactstrap";
import { useNavigate } from "react-router-dom";
import {
    DashboardPageHeader,
    EmptyState,
    StandardTableToolbar,
    ResponsiveTabs, TableSkeleton } from "../../Components/Common/DashboardKit";
import api from "../../api";

const STATUS_TABS = [
    { key: "",                label: "All" },
    { key: "Submitted",       label: "Submitted" },
    { key: "Approved",        label: "Approved" },
    { key: "Rejected",        label: "Rejected" },
    { key: "Closed",          label: "Closed" },
];

const StatusBadge = ({ status }) => {
    const s = String(status || "").toLowerCase();
    const color =
        s === "approved"  ? "success" :
        s === "submitted" || s === "pending" || s === "under review" ? "warning" :
        s === "rejected"  ? "danger" :
        s === "closed"    ? "secondary" : "info";
    return <Badge color={color} pill style={{ fontSize: 11 }}>{status || "—"}</Badge>;
};
const fmtDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleDateString(); } catch { return "—"; }
};

const DeptProjects = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("");
    const [items, setItems] = useState([]);
    const [scope, setScope] = useState("TEAM");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setError(null);
        const params = activeTab ? { status: activeTab } : {};
        api.get("ProjectApproval/my-department", { params })
            .then(({ data }) => {
                if (!alive) return;
                setItems(Array.isArray(data?.Items) ? data.Items : []);
                setScope(data?.Scope || "TEAM");
            })
            .catch((e) => { if (alive) setError(e?.response?.data?.message || e?.message || "Failed to load"); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [activeTab]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter((r) =>
            String(r.ProjectName ?? "").toLowerCase().includes(q)
            || String(r.CreatedByName ?? "").toLowerCase().includes(q)
            || String(r.Purpose ?? "").toLowerCase().includes(q)
        );
    }, [items, search]);

    // Round 36 — EPO-style per-column filters + click-to-sort + CSV.
    const [columnFilters, setColumnFilters] = useState({});
    const tblColumns = useMemo(() => [
        { id: "ProjectID",   label: "ID",      type: "text", get: (r) => String(r.ProjectID ?? "") },
        { id: "ProjectName", label: "Project", type: "text", get: (r) => r.ProjectName || "" },
        { id: "CreatedByName", label: "Created By", type: "text", get: (r) => r.CreatedByName || r.CreatedBy || "" },
        { id: "TeamLeader",  label: "Team Leader", type: "text", get: (r) => String(r.TeamLeader ?? "") },
        { id: "Type",        label: "Type",    type: "text", get: (r) => r.Type || "" },
        { id: "Status",      label: "Status",  type: "text", get: (r) => r.Status || "" },
        { id: "Requested",   label: "Requested", type: "date", get: (r) => r.RequestDate || "" },
        { id: "NeedBy",      label: "Need By",   type: "date", get: (r) => r.NeedByDate || "" },
        { id: "act", label: "", type: "none" },
    ], []);
    const filteredBase = applyColumnFilters(filtered, tblColumns, columnFilters);
    const { sorted: view, sort, toggleSort } = useTableSort(filteredBase, tblColumns);

    const subtitle = scope === "ALL"
        ? `All projects (${items.length})`
        : `Your team's projects (${items.length})`;

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    title="My Department's Projects"
                    breadcrumb={[{ label: "Project Approval", to: "/workflows?wf=project" }, { label: "My Department's Projects" }]}
                    subtitle={subtitle}
                    breadcrumbItems={[{ label: "Projects" }, { label: "My Department" }]}
                />

                {error && <Alert color="danger" className="mt-2">{error}</Alert>}

                <ResponsiveTabs
                    items={STATUS_TABS}
                    activeKey={activeTab}
                    onChange={setActiveTab}
                />

                <Card className="mt-3">
                    <CardBody>
                        <StandardTableToolbar
                            right={
                                {/* Round 36 — search replaced by the per-column filter row */}
                            }
                        />

                        {loading ? (
                            <div className="table-responsive">
                                <table className="table align-middle mb-0">
                                    <tbody><TableSkeleton rows={6} columns={10} /></tbody>
                                </table>
                            </div>
                        ) : view.length === 0 ? (
                            <EmptyState
                                title="No projects to show"
                                description={scope === "TEAM"
                                    ? "Neither you nor your direct reports have projects matching this filter."
                                    : "No projects matching this filter."
                                }
                            />
                        ) : (
                            <div className="table-responsive">
                                {/* Round 36 — CSV of the filtered + sorted list */}
                                <div className="d-flex justify-content-end mb-2">
                                    <button type="button" className="btn btn-soft-primary btn-sm"
                                        onClick={() => exportRowsToCsv("projects-deptprojects.csv", tblColumns.filter((c) => c.type !== "none"), view)}>
                                        <i className="ri-download-2-line align-bottom me-1" />Export CSV ({view.length})
                                    </button>
                                </div>
                                <table className="table table-striped table-bordered mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            {tblColumns.map((c) => (
                                                <SortableTh key={c.id} col={c} sort={sort} onToggle={toggleSort} />
                                            ))}
                                        </tr>
                                        <ColumnFilterRow columns={tblColumns} filters={columnFilters} onChange={setColumnFilters} />
                                    </thead>
                                    <tbody>
                                        {view.map((r) => (
                                            <tr key={r.ProjectID}>
                                                <td>{r.ProjectID}</td>
                                                <td style={{ maxWidth: 280 }}>
                                                    <div className="fw-semibold">{r.ProjectName}</div>
                                                    {r.Purpose && (
                                                        <div className="text-muted small text-truncate" title={r.Purpose}>
                                                            {r.Purpose}
                                                        </div>
                                                    )}
                                                </td>
                                                <td>{r.CreatedByName || r.CreatedBy}</td>
                                                <td>{r.TeamLeader}</td>
                                                <td>{r.Type || "—"}</td>
                                                <td><StatusBadge status={r.Status} /></td>
                                                <td>{fmtDate(r.RequestDate)}</td>
                                                <td>{fmtDate(r.NeedByDate)}</td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="btn btn-link btn-sm p-0"
                                                        onClick={() => navigate(`/apps-projects-overview?id=${r.ProjectID}`)}
                                                    >View</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default DeptProjects;
